<!--
  ReferenceInput.vue

  Autocomplete input for reference fields. `modelValue` stores ids;
  `field.value(item)` returns the target entity (or array) to render. Serves
  junctions (link/tagging/attachment) too — they inherit it as their `form`
  renderer, emitting the target-id set; the host reconciles the rows
  (useRecord.updateField) or, in a filter, resolves the ids (useFilter.enrich).

  Used directly as the `form` slot (records and filters). The `cell` slot is
  `ReferenceField.vue` — a separate emissive component built for the grid's
  active/editing overlay chrome, sharing this component's `REFERENCE_REGISTRY`
  dispatch.

  Per-subtype behavior — which chip element renders and how it searches —
  lives in `REFERENCE_REGISTRY` (./registry.ts), keyed by `ReferenceType`; a
  subtype with no entry takes its parent's (`referenceEntry`).
  Adding a new reference type means adding one entry there. `field.isArray`
  drives single- vs multi-select:

    - multi: chips + a trailing search box; selecting appends, backspace/× removes.
    - single: one chip. Click it to edit — the chip is replaced by the search
      box seeded with the current name (all selected, searched immediately), so
      one keystroke replaces it or Backspace clears it. Picking swaps the chip
      back. Empty single fields show the search box directly.
-->
<template>
  <!-- SINGLE-VALUE: one chip, click to edit as pre-selected text -->
  <div
    v-if="!field?.isArray"
    ref="rootEl"
    class="input chip-input reference-input is-single"
    :class="[`is-${size}`, { 'is-searching': showSearch, 'is-scoped': !readonly && !!scopeNote }]"
    @click="enterEdit"
    @keydown.esc="exitEdit"
  >
    <span v-if="!readonly && scopeNote" class="scope-icon" :title="scopeNote">
      <font-awesome-icon icon="filter" />
    </span>
    <AutocompleteInput
      v-if="showSearch"
      ref="editor"
      :results="searchResults"
      :placeholder="placeholder"
      :initial-value="editInitial"
      :size="size"
      @search="handleSearch"
      @select="handleSelectSingle"
    >
      <!-- Clear lives in the search box (in place of the resting chevron), so it
           only appears once you click in — never a hover target beside the chevron. -->
      <button
        v-if="hasValue"
        type="button"
        class="reference-input-clear"
        title="Clear"
        @click.stop="clearSingle"
      >
        <font-awesome-icon icon="circle-xmark" />
      </button>
      <template #suggestion="{ result }">
        <component
          :is="referenceType.element"
          :value="entityById(result[0])"
          readonly
          fixed
          :size="size"
        />
      </template>
    </AutocompleteInput>

    <template v-else>
      <!-- The chip stays a navigable link (its @click.stop keeps the box's
           enterEdit from firing). Clicking the surrounding whitespace / chevron
           opens the picker instead. -->
      <template v-for="entity in entities" :key="entity.id">
        <component
          :is="referenceType.element"
          :value="entity"
          readonly
          :fixed="fixed"
          :size="size"
        />
        <span v-if="isOutside(entity.id)" class="outside-filter" :title="OUTSIDE_FILTER">
          <font-awesome-icon icon="triangle-exclamation" />
        </span>
      </template>
      <component
        :is="referenceType.element"
        v-for="id in missingIds"
        :key="`missing-${id}`"
        :value="{ id, name: `Unknown ID: ${id}` }"
        readonly
        :size="size"
      />
      <span v-if="!readonly && !!referenceType.search" class="reference-input-caret">
        <font-awesome-icon icon="chevron-down" />
      </span>
    </template>
  </div>

  <!-- MULTI-VALUE: chip row + trailing search box -->
  <div v-else class="input chip-input reference-input" :class="`is-${size}`">
    <span v-if="!readonly && scopeNote" class="scope-icon" :title="scopeNote">
      <font-awesome-icon icon="filter" />
    </span>
    <template v-for="entity in entities" :key="entity.id">
      <component
        :is="referenceType.element"
        :value="entity"
        :readonly="readonly"
        :fixed="fixed"
        :size="size"
        @remove="removeEntity(entity.id)"
      />
      <span v-if="isOutside(entity.id)" class="outside-filter" :title="OUTSIDE_FILTER">
        <font-awesome-icon icon="triangle-exclamation" />
      </span>
    </template>
    <component
      :is="referenceType.element"
      v-for="id in missingIds"
      :key="`missing-${id}`"
      :value="{ id, name: `Unknown ID: ${id}` }"
      readonly
      :size="size"
    />
    <AutocompleteInput
      v-if="!readonly"
      :results="searchResults"
      :placeholder="placeholder"
      :size="size"
      clear-on-select
      @search="handleSearch"
      @select="handleSelect"
    >
      <template #suggestion="{ result }">
        <component
          :is="referenceType.element"
          :value="entityById(result[0])"
          readonly
          fixed
          :size="size"
        />
      </template>
    </AutocompleteInput>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from "vue"
import { type AugerField, hasActiveConditions, withAppearance } from "@auger/core/fields"
import AutocompleteInput from "@auger/ui/components/AutocompleteInput.vue"
import {
  OUTSIDE_FILTER,
  REFERENCE_SCOPE,
  type ReferenceEntity,
  referenceEntry,
  searchOptions,
} from "./registry"

const props = withDefaults(
  defineProps<{
    modelValue?: number | string | (number | string)[] | null
    /** The row this picker belongs to. `field.value(item)` extracts the
     *  target entity (or array) for the chips. */
    item?: Record<string, unknown> | null
    field?: AugerField
    readonly?: boolean
    size?: string
    placeholder?: string
    fixed?: boolean // Render chips as non-navigating
  }>(),
  {
    modelValue: null,
    item: null,
    field: undefined,
    readonly: false,
    size: "normal",
    placeholder: undefined,
    fixed: false,
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: number | string | (number | string)[] | null]
  /** The record just picked, for a host with no saved row to read chips from. */
  picked: [entity: ReferenceEntity]
}>()

// REFERENCE TYPE ==============================================================

const referenceType = computed(() => referenceEntry(props.field?.type))

const placeholder = computed(() => props.placeholder ?? referenceType.value.placeholder)
const scopeNote = computed(() => REFERENCE_SCOPE.filterNote(props.field))

// Only a filtered field asks which of its links fall outside the filter.
const { isOutside } = hasActiveConditions(props.field?.filter)
  ? REFERENCE_SCOPE.linksOutsideFilter(() => props.field)
  : { isOutside: () => false }

// CHIPS =======================================================================

const searchResults = ref<[number | string, string][]>([])
const searchEntities = ref<Map<string | number, ReferenceEntity>>(new Map())

const selectedIds = computed<(number | string)[]>(() => {
  if (props.modelValue == null) return []
  return Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
})

const entities = computed<ReferenceEntity[]>(() => {
  const resolved = props.field?.value(props.item)
  if (resolved == null) return []
  const arr = Array.isArray(resolved) ? resolved : [resolved]
  return arr.filter((e): e is ReferenceEntity => !!e && typeof e === "object" && "id" in e)
})

const missingIds = computed(() => {
  const resolvedIds = new Set(entities.value.map((e) => String(e.id)))
  return selectedIds.value.filter((id) => !resolvedIds.has(String(id)))
})

// SEARCH ======================================================================

function handleSearch(query: string) {
  const search = referenceType.value.search
  if (!query || query.length < 2 || !search) {
    searchResults.value = []
    searchEntities.value.clear()
    return
  }
  search(query, searchOptions(props.field))
    .then((found) => {
      // A picked result is rendered as a chip straight away, so each record row
      // gets its appearance (icon, colour) as a resolved value would have.
      const rows = found.map(withAppearance)
      // Multi-value hides already-added chips; single-value keeps the current
      // value in results so its exact name still matches when you re-type it.
      const filtered = props.field?.isArray
        ? rows.filter((row) => !selectedIds.value.includes(row.id))
        : rows
      searchEntities.value = new Map(filtered.map((row) => [row.id, row]))
      searchResults.value = filtered.map((row) => [row.id, row.name ?? String(row.id)])
    })
    .catch((err) => {
      console.error("ReferenceInput: search failed", err)
      searchResults.value = []
    })
}

function entityById(id: number | string): ReferenceEntity | undefined {
  return searchEntities.value.get(id) ?? entities.value.find((e) => e.id === id)
}

// MULTI-VALUE ADD / REMOVE ====================================================

function handleSelect(result: [number | string, string]) {
  const entity = entityById(result[0])
  if (!entity) return
  if (selectedIds.value.includes(entity.id)) return
  emit("picked", entity)
  emit("update:modelValue", [...selectedIds.value, entity.id])
  searchResults.value = []
  searchEntities.value.clear()
}

function removeEntity(id: number | string) {
  const next = selectedIds.value.filter((existing) => existing !== id)
  emit("update:modelValue", next)
}

// SINGLE-VALUE EDIT ===========================================================
// The box shows one chip at rest and swaps to the search box on click, seeded
// with the current name (selected + searched) so it's quick to replace or clear.

const editor = ref<InstanceType<typeof AutocompleteInput> | null>(null)
const editing = ref(false)
const editInitial = ref("")

const hasValue = computed(() => entities.value.length > 0 || missingIds.value.length > 0)
const editable = computed(() => !props.readonly && !!referenceType.value.search && hasValue.value)
// The search box is shown while editing a value, or whenever the field is empty.
const showSearch = computed(() => !props.readonly && (editing.value || !hasValue.value))

function enterEdit() {
  if (!editable.value || editing.value) return
  editInitial.value = entities.value[0]?.name ?? String(missingIds.value[0] ?? "")
  editing.value = true
  document.addEventListener("mousedown", onDocumentMousedown, true)
  nextTick(() => {
    editor.value?.focus(true) // focus + select all
    if (editInitial.value) handleSearch(editInitial.value)
  })
}

function exitEdit() {
  if (!editing.value) return
  editing.value = false
  document.removeEventListener("mousedown", onDocumentMousedown, true)
}

// Click outside the box (and outside the teleported results) reverts to the
// chip. A click on a result lands inside `.autocomplete-results`, so it selects
// first and this doesn't fire.
const rootEl = ref<HTMLElement | null>(null)
function onDocumentMousedown(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (rootEl.value?.contains(target) || target.closest?.(".autocomplete-results")) return
  exitEdit()
}

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentMousedown, true)
})

function handleSelectSingle(result: [number | string, string]) {
  const entity = entityById(result[0])
  if (entity) emit("picked", entity)
  emit("update:modelValue", result[0])
  searchResults.value = []
  searchEntities.value.clear()
  exitEdit()
}

function clearSingle() {
  emit("update:modelValue", null)
  // Reset the seeded text too (clear() alone leaves the old name, since
  // `initial-value` still holds it), so the box goes empty for the next search.
  editInitial.value = ""
  editor.value?.clear()
  exitEdit()
}
</script>

<style>
.reference-input.is-single {
  display: flex;
  align-items: center;
  gap: var(--auger-size-6);
  cursor: pointer;
}

/* The chip's vertical-overflow margin keeps the row at control-height. Without
   it the 24px chip grows this auto-height (`.chip-input`) box past the empty
   state's min-height, so the field is taller with a chip than without one. The
   intended `.chip-input .tag` overflow margin is overridden here, so reassert it
   (!important to beat that override) — scoped to the single-value box only. */
.reference-input.is-single .tag {
  margin-block: var(--auger-chip-vertical-overflow) !important;
}

/* Chevron is the only resting indicator — it reads as a select (click to
   change), not a delete. */
.reference-input-caret {
  margin-inline-start: auto;
  color: var(--auger-text-weak);
  font-size: 0.75em;
  display: inline-flex;
}

/* While the search box is shown, float the autocomplete to fill the border box
   in both axes (`inset: 0`). The teleported results measure their width and top
   from that inner box, so it must reach the full width AND full height —
   otherwise the dropdown is narrower than the input and anchors above the box
   bottom, overlapping it. (`.chip-input` is a wrapping flexbox, so align-stretch
   only fills the text-height flex line, not the box — hence the absolute fill.)
   Text padding moves onto the input so it still sits ~8px in, centered. */
.reference-input.is-single.is-searching {
  position: relative;
}

.reference-input.is-single.is-searching .autocomplete {
  position: absolute;
  /* Inset 2px horizontally so AutocompleteInput's `-3px/+6px` dropdown nudge
     lands exactly on the input's edges rather than 2px past them. */
  inset: 0 2px;
}

.reference-input.is-single.is-searching .autocomplete-input-container,
.reference-input.is-single.is-searching .autocomplete-input {
  height: 100%;
}

.reference-input.is-single.is-searching .autocomplete-input {
  padding-left: 8px;
  padding-right: 24px; /* room for the clear × */
}

/* A filtered field's icon sits at the start of the box. The box's gap is for
   the chip and the chevron; the icon sits closer to the chip. While searching
   it stays above the floated search box, whose text starts where the chip's
   name sat (past the icon, the 4px gap and the chip's own padding), so the
   text doesn't move as you click in. */
.reference-input.is-single .scope-icon {
  position: relative;
  z-index: 2;
  margin-right: calc(4px - var(--auger-size-6));
}

.reference-input.is-single.is-searching.is-scoped .autocomplete-input {
  padding-left: 33px;
}

/* Clear appears only while editing, pinned to the right of the search box in
   place of the chevron — so it's never a hover target beside it at rest. */
.reference-input.is-single .reference-input-clear {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: var(--auger-text-weak);
  display: inline-flex;

  &:hover {
    color: var(--auger-text);
  }
}
</style>
